import { useCallback, useEffect, useRef, useState } from 'react';
import {
  closeSerialPortSafe,
  consumeScannerStreamBuffer,
  findBarcodePort,
  listSerialPortsSafe,
  normalizeScannerPayload,
  onSerialDataSafe,
  openSerialPortSafe,
  type SerialPortInfo,
} from './barcodeScanner';

type UseBarcodeScannerOptions = {
  enabled?: boolean;
  pollIntervalMs?: number;
  baudRate?: number;
  /** Transform or drop a complete scan before onScan (return null to ignore). */
  mapScan?: (value: string) => string | null;
  onScan?: (value: string) => void;
};

type UseBarcodeScannerResult = {
  connectionActive: boolean;
  connectionPort: SerialPortInfo | undefined;
  scannerError: string;
  lastScanValue: string;
  clearLastScanValue: () => void;
  /**
   * Only one scanner field can hold the shared physical serial port at a time — only
   * the current owner opens/polls the port, and only it reports itself connected and
   * receives scans. Wire this to the field's `onFocus` so clicking into a field hands
   * ownership to it immediately: the previous owner releases the port right away and
   * the new owner opens it, instead of racing for it.
   */
  claim: () => void;
};

let scannerOwnerId: string | null = null;
let nextScannerInstanceSeq = 1;
const scannerOwnerListeners = new Set<() => void>();

function claimScannerOwner(id: string): void {
  if (scannerOwnerId === id) return;
  scannerOwnerId = id;
  scannerOwnerListeners.forEach((listener) => listener());
}

function releaseScannerOwnerIfHeld(id: string): void {
  if (scannerOwnerId !== id) return;
  scannerOwnerId = null;
  scannerOwnerListeners.forEach((listener) => listener());
}

export default function useBarcodeScanner(options?: UseBarcodeScannerOptions): UseBarcodeScannerResult {
  const enabled = options?.enabled !== false;
  const pollIntervalMs = options?.pollIntervalMs ?? 1000;
  const baudRate = options?.baudRate ?? 9600;
  const onScan = options?.onScan;
  const mapScan = options?.mapScan;

  const instanceIdRef = useRef('');
  if (!instanceIdRef.current) instanceIdRef.current = `scanner-${nextScannerInstanceSeq++}`;
  const instanceId = instanceIdRef.current;

  const [connectionActive, setConnectionActive] = useState(false);
  const [connectionPort, setConnectionPort] = useState<SerialPortInfo | undefined>(undefined);
  const [scannerError, setScannerError] = useState('');
  const [lastScanValue, setLastScanValue] = useState('');
  const [, setOwnerTick] = useState(0);
  const connectionActiveRef = useRef(false);
  const connectedPathRef = useRef<string | null>(null);
  const onScanRef = useRef<((value: string) => void) | undefined>(onScan);
  const mapScanRef = useRef<((value: string) => string | null) | undefined>(mapScan);
  const streamBufferRef = useRef('');
  const flushTimerRef = useRef<number | null>(null);
  const isOwnerRef = useRef(false);

  useEffect(() => {
    connectionActiveRef.current = connectionActive;
  }, [connectionActive]);

  useEffect(() => {
    const listener = (): void => setOwnerTick((n) => n + 1);
    scannerOwnerListeners.add(listener);
    return () => {
      scannerOwnerListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      releaseScannerOwnerIfHeld(instanceId);
      return;
    }
    if (scannerOwnerId === null) {
      claimScannerOwner(instanceId);
    }
  });

  useEffect(() => () => releaseScannerOwnerIfHeld(instanceId), [instanceId]);

  const isOwner = scannerOwnerId === instanceId;
  useEffect(() => {
    isOwnerRef.current = isOwner;
  }, [isOwner]);

  const claim = useCallback(() => {
    claimScannerOwner(instanceId);
  }, [instanceId]);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    mapScanRef.current = mapScan;
  }, [mapScan]);

  const handleClosePort = useCallback(async (): Promise<void> => {
    const res = await closeSerialPortSafe();
    connectedPathRef.current = null;
    setConnectionActive(false);
    setScannerError(res.success ? '' : String(res.message || 'Failed to close scanner'));
  }, []);

  const handleOpenPort = useCallback(
    async (path?: string | null): Promise<void> => {
      if (!path) return;
      const res = await openSerialPortSafe(path, baudRate);
      if (res.success) {
        connectedPathRef.current = path;
        setConnectionActive(true);
        setScannerError('');
      } else {
        connectedPathRef.current = null;
        setConnectionActive(false);
        setScannerError(String(res.message || 'Failed to open scanner'));
      }
    },
    [baudRate],
  );

  useEffect(() => {
    // Only the current owner may hold the shared physical port — every other mounted
    // instance must stay hands-off so a claim doesn't have to wait for a stray
    // non-owner's stale connection to time out (or the component to unmount) before
    // the new owner can actually open it.
    if (!enabled || !isOwner) {
      void handleClosePort();
      setConnectionPort(undefined);
      return;
    }
    if (!window.serialAPI) return;

    let alive = true;
    let timer: number | undefined;

    const run = async (): Promise<void> => {
      const ports = await listSerialPortsSafe();
      const match = findBarcodePort(ports);
      if (!alive) return;

      if (!match?.path) {
        setConnectionPort(undefined);
        if (connectionActiveRef.current) {
          await handleClosePort();
        }
      } else {
        setConnectionPort(match);
        const pathChanged = connectedPathRef.current !== match.path;
        if (!connectionActiveRef.current || pathChanged) {
          if (connectionActiveRef.current && pathChanged) {
            await handleClosePort();
          }
          await handleOpenPort(match.path);
        }
      }

      if (!alive) return;
      timer = window.setTimeout(() => {
        void run();
      }, pollIntervalMs);
    };

    void run();
    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
      void handleClosePort();
    };
  }, [enabled, isOwner, handleClosePort, handleOpenPort, pollIntervalMs]);

  useEffect(() => {
    if (!enabled) return;

    const emitScan = (payload: string): void => {
      const cleaned = normalizeScannerPayload(payload);
      if (!cleaned) return;
      const mapped = mapScanRef.current?.(cleaned);
      if (mapped === null) return;
      const scanValue = mapped ?? cleaned;
      setLastScanValue(scanValue);
      onScanRef.current?.(scanValue);
    };

    const flushBufferedScan = (): void => {
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      const pending = streamBufferRef.current;
      streamBufferRef.current = '';
      if (!pending) return;
      emitScan(pending);
    };

    const scheduleBufferedFlush = (): void => {
      if (flushTimerRef.current) window.clearTimeout(flushTimerRef.current);
      // Scanners often deliver the full barcode in one burst; short idle = end of scan.
      flushTimerRef.current = window.setTimeout(flushBufferedScan, 80);
    };

    const unsubscribe = onSerialDataSafe((_event: unknown, data: string) => {
      if (!connectionActiveRef.current) return;
      if (!isOwnerRef.current) return;
      if (!data) return;

      const { nextBuffer, lines } = consumeScannerStreamBuffer(streamBufferRef.current, data);
      streamBufferRef.current = nextBuffer;
      for (const line of lines) {
        emitScan(line);
      }
      if (streamBufferRef.current) {
        scheduleBufferedFlush();
      }
    });

    return () => {
      unsubscribe();
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      streamBufferRef.current = '';
    };
  }, [enabled]);

  const clearLastScanValue = useCallback(() => {
    setLastScanValue('');
  }, []);

  return {
    connectionActive: connectionActive && isOwner,
    connectionPort,
    scannerError,
    lastScanValue,
    clearLastScanValue,
    claim,
  };
}
